import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';

import ReactEChartsCore from 'echarts-for-react/lib/core';
import { PieChart } from 'echarts/charts';
import * as echarts from 'echarts/core';
import {
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';

import { getColor, numberFormatter, formatCurrency } from '../../../helpers/utils';
import SnapshotService from '../services';
import CardEmptyResult from 'components/common/pageComponents/CardEmptyResult';
import { PiPackageDuotone } from 'react-icons/pi';

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  PieChart,
  CanvasRenderer,
  LegendComponent
]);

const TopSellingProductsChart = ({ limit = 10, period = 'total', startDate, endDate, hidePeriodSelector = false }) => {
  const [currency_symbol, setCurrencySymbol] = useState('$');
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const getData = useCallback(() => {
    setIsLoading(true);
    let params = {};
    if (hidePeriodSelector && startDate && endDate) {
      params = { limit, start_date: startDate, end_date: endDate, sort_order: 'desc' };
    } else {
      params = {
      limit: limit,
      sort_by: period === 'total' ? 'total_revenue' :
               period === 'current_month' ? 'current_month_revenue' :
               period === 'last_30_days' ? 'last_30_days_revenue' :
               period === 'last_90_days' ? 'last_90_days_revenue' :
               period === 'current_year' ? 'current_year_revenue' : 'total_revenue',
      sort_order: 'desc'
    };
    }

    SnapshotService.getItemSnapshots(params).then((response) => {
      try {
        if (response.data?.success && response.data?.data) {
          const { data: chartData, currency } = response.data.data;
          setData(chartData || []);
          setCurrencySymbol(currency?.currency_symbol || '$');
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Error fetching top selling products data:', error);
        setIsLoading(false);
      }
    }).catch(error => {
      console.error('Error fetching top selling products data:', error);
      setIsLoading(false);
    });
  }, [limit, period, startDate, endDate, hidePeriodSelector]);

  useEffect(() => {
    getData();
  }, [getData]);

  // Prepare pie chart data (name-value pairs)
  const pieData = useMemo(() => {
    return data.map((item, index) => {
      const name = item.item_name || 'N/A';
      let revenue = 0;

      if (hidePeriodSelector && startDate && endDate) {
        revenue = parseFloat(item.period_revenue) || 0;
      } else {
        switch (period) {
        case 'current_month':
          revenue = parseFloat(item.current_month_revenue) || 0;
          break;
        case 'last_30_days':
          revenue = parseFloat(item.last_30_days_revenue) || 0;
          break;
        case 'last_90_days':
          revenue = parseFloat(item.last_90_days_revenue) || 0;
          break;
        case 'current_year':
          revenue = parseFloat(item.current_year_revenue) || 0;
          break;
        default:
          revenue = parseFloat(item.total_revenue) || 0;
        }
      }

      return {
        name: name.length > 25 ? name.substring(0, 25) + '...' : name,
        value: revenue,
        item: item // Store full item data for tooltip
      };
    }).filter(item => item.value > 0); // Filter out zero values
  }, [data, period, hidePeriodSelector, startDate, endDate]);

  const tooltipFormatter = useCallback((params) => {
    if (!params || !params.data) {
      return '';
    }

    const item = params.data.item;
    if (!item) return '';

    const revenue = params.value || 0;
    const salesCount =
      hidePeriodSelector && startDate && endDate
        ? undefined
        : period === 'current_month' ? item.current_month_sales_count :
                      period === 'last_30_days' ? item.last_30_days_sales_count :
                      period === 'last_90_days' ? item.last_90_days_sales_count :
                      period === 'current_year' ? item.current_year_sales_count :
                      item.total_sales_count;

    const percentage = params.percent || 0;

    return `
      <div style="padding: 8px;">
        <div style="font-weight: 600; margin-bottom: 4px;">${item.item_name || params.name}</div>
        <div style="margin: 4px 0;">
          Sales Revenue: ${formatCurrency(revenue, currency_symbol)}
        </div>
        <div style="margin: 4px 0;">
          Percentage: ${percentage.toFixed(1)}%
        </div>
        ${salesCount != null ? `<div style="margin: 4px 0;">Sales Count: ${salesCount || 0}</div>` : ''}
      </div>
    `;
  }, [currency_symbol, period, hidePeriodSelector, startDate, endDate]);

  // Generate color palette for pie chart
  const colorPalette = useMemo(() => {
    const colors = [
      getColor('primary'),
      getColor('info'),
      getColor('success'),
      getColor('warning'),
      getColor('danger'),
      '#722ed1', // purple
      '#13c2c2', // cyan
      '#fa8c16', // orange
      '#eb2f96', // pink
      '#52c41a'  // green
    ];
    return colors;
  }, []);

  const getOptions = useMemo(() => ({
    tooltip: {
      trigger: 'item',
      formatter: tooltipFormatter,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      borderColor: 'transparent',
      textStyle: { color: '#fff' }
    },
    // title: {
    //   text: 'Top selling Products',
    //   left: 'top',
    //   top: 10,
    //   textStyle: {
    //     color: getColor('primary'),
    //     fontSize: 16,
    //     fontWeight: 'bold'
    //   }
    // },
    legend: {
      orient: 'vertical',
      left: 'left',
      top: 'top',
      textStyle: {
        color: getColor('gray-600'),
        fontSize: 12
      },
      formatter: (name) => {
        const item = pieData.find(d => d.name === name);
        if (item) {
          return `${name}\n${formatCurrency(item.value, currency_symbol)}`;
        }
        return name;
      },
      itemGap: 8
    },
    series: [
      {
        name: 'Sales Revenue',
        type: 'pie',
        radius: ['40%', '70%'], // Donut chart style
        center: ['60%', '55%'],
        // avoidLabelOverlap: true,
        itemStyle: {
        //   borderRadius: 4,
        //   borderColor: '#fff',
        //   borderWidth: 2
        },
        label: {
          show: true,
          formatter: (params) => {
            return `${params.name}\n${formatCurrency(params.value, currency_symbol)}\n(${params.percent}%)`;
          },
          fontSize: 11,
          color: getColor('gray-700')
        },
        labelLine: {
          show: true,
          length: 15,
          length2: 10
        },
        emphasis: {
          label: {
            show: true,
            fontSize: 13,
            fontWeight: 'bold'
          },
          itemStyle: {
            // shadowBlur: 10,
            shadowOffsetX: 0,
            // shadowColor: 'rgba(0, 0, 0, 0.5)'
          }
        },
        data: pieData.map((item, index) => ({
          ...item,
          itemStyle: {
            color: colorPalette[index % colorPalette.length]
          }
        }))
      }
    ]
  }), [pieData, currency_symbol, tooltipFormatter, colorPalette]);

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Spin indicator={<LoadingOutlined spin />} />
      </div>
    );
  }

  const hasData = pieData.length > 0;

  if (!hasData) {
    return (
      <CardEmptyResult
        title="No top selling products data"
        subtitle="There is no sales data available to display top selling products."
        icon={<PiPackageDuotone className="fs-2 text-secondary" />}
      />
    );
  }

  return (
    <ReactEChartsCore
      echarts={echarts}
      option={getOptions}
      style={{ height: '400px', width: '100%' }}
    />
  );
};

export default TopSellingProductsChart;

